import {
    SCHEDULE_APPLICATION_NON_CONFLICTING_STATUSES,
    ScheduleApplicationStatus,
} from "../types/domains/accounting/schedule";
import { pluralize, pluralizeWithCount } from "./string";

/**
 * A recognition schedule applied to a transaction owns the transaction's
 * Accrual ledger account on the schedule's ledger: the schedule's journal
 * entries recognize out of that holding account. The lock holds for every
 * live application (active, paused, stopped, accelerated, released,
 * completed), since all of them leave the holding account coded and have
 * posted entries. A rule-suggested draft is not a schedule yet, and retired
 * applications have restored the previous coding.
 */
export const SCHEDULE_FIELD_LOCK_TOOLTIP =
    "This account is set by the recognition schedule. Remove or edit the schedule to change it.";

export const SCHEDULE_FIELD_LOCK_ERROR_MESSAGE =
    "Can't change the Accrual ledger. It's set by the recognition schedule. Remove or edit the schedule to change it.";

export const SCHEDULE_FIELD_LOCK_BULK_REASON =
    "the Accrual ledger is set by a recognition schedule";

/**
 * A live schedule recognizes in the transaction's entity, so moving the
 * transaction would leave the schedule and its entries behind.
 */
export const SCHEDULE_ENTITY_LOCK_MESSAGE =
    "Remove the recognition schedule before moving this transaction to another entity";

export const SCHEDULE_ENTITY_LOCK_BULK_REASON =
    "a recognition schedule must be removed first";

export function buildScheduleAccountEntityLockMessage(
    scheduledTransactionCount: number,
): string {
    return `Remove the recognition ${pluralize(
        "schedule",
        scheduledTransactionCount,
    )} from ${pluralizeWithCount(
        "transaction",
        scheduledTransactionCount,
    )} before moving this account to another entity`;
}

export function isScheduleLockingAccrualLedger(
    status: ScheduleApplicationStatus,
): boolean {
    return !SCHEDULE_APPLICATION_NON_CONFLICTING_STATUSES.some(
        (nonConflicting) => nonConflicting === status,
    );
}

export interface ScheduleFieldLockSource {
    status: ScheduleApplicationStatus;
    ledgerId: string;
}

export interface ScheduleFieldLockParams {
    schedule: ScheduleFieldLockSource | null | undefined;
    ledgerId?: string | null;
}

export interface ScheduleFieldLock {
    isLocked: boolean;
    tooltip: string | undefined;
}

export interface ScheduleEntityLockParams {
    schedule: Pick<ScheduleFieldLockSource, "status"> | null | undefined;
}

export function getScheduleEntityLock({
    schedule,
}: ScheduleEntityLockParams): ScheduleFieldLock {
    const isLocked =
        !!schedule && isScheduleLockingAccrualLedger(schedule.status);
    return {
        isLocked,
        tooltip: isLocked ? SCHEDULE_ENTITY_LOCK_MESSAGE : undefined,
    };
}

export interface EntityPickerLockParams extends ScheduleEntityLockParams {
    isPeriodLocked: boolean;
}

/** A period lock wins over the schedule lock and shows no tooltip. */
export function getEntityPickerLock({
    schedule,
    isPeriodLocked,
}: EntityPickerLockParams): ScheduleFieldLock {
    return isPeriodLocked
        ? { isLocked: true, tooltip: undefined }
        : getScheduleEntityLock({ schedule });
}

export function getScheduleFieldLock({
    schedule,
    ledgerId,
}: ScheduleFieldLockParams): ScheduleFieldLock {
    const isLocked =
        !!schedule &&
        isScheduleLockingAccrualLedger(schedule.status) &&
        (ledgerId == null || schedule.ledgerId === ledgerId);
    return {
        isLocked,
        tooltip: isLocked ? SCHEDULE_FIELD_LOCK_TOOLTIP : undefined,
    };
}
